import angular from 'angular';
import 'angular-ui-grid/ui-grid.js';
/**
 * This will get extracted into its own CSS bundle. Anything that is using CSMCommonWeb
 * will have access to the ui-grid's CSS. If you come across an application that is not
 * properly loading ui-grid's CSS, it may indicate that the application is not using
 * CSMCommonWeb and therefore needs to import it directly within that application.
 */
import 'angular-ui-grid/ui-grid.css';
import keyCodes from '../../util/keycodes/keycodes';

export default angular
  .module('csm.vendors.ui-grid', [keyCodes, 'ui.grid'])
  .config(vendorsGridConfig)
  .config(uiGridCellNavServiceConfig)
  .config(rowSorterConfig)
  .service('CommonUIGridTemplates', CommonUIGridTemplatesService)
  .directive('uiGridCellnav', uiGridCellnavDirective)
  .directive('uiGridEditInput', uiGridEditInputDirective)
  .directive('uiGridEditor', uiGridEditorDirective)
  .directive('uiGridEditTypeahead', uiGridEditTypeaheadDirective)
  .directive('uiGridEditLazyTypeahead', uiGridEditLazyTypeaheadDirective)
  .directive('uiGridHeaderCell', uiGridHeaderCellDirective)
  .factory('GridState', GridStateFactory)
  .run(runVendorUiGrid)
  .run(runExporterFieldCallbackFix).name;

vendorsGridConfig.$inject = ['$injector', '$provide'];
uiGridCellNavServiceConfig.$inject = ['$injector', '$provide'];
rowSorterConfig.$inject = ['$injector', '$provide'];
uiGridEditInputDirective.$inject = [
  'gridUtil',
  'uiGridConstants',
  'uiGridEditConstants',
  '$timeout',
  'uiGridEditService'
];
uiGridEditorDirective.$inject = ['uiGridEditConstants'];
uiGridEditTypeaheadDirective.$inject = [
  'uiGridEditConstants',
  '$timeout',
  'KEYCODES'
];
uiGridEditLazyTypeaheadDirective.$inject = [
  '$compile',
  '$parse',
  '$timeout',
  'uiGridEditConstants',
  'KEYCODES'
];
uiGridHeaderCellDirective.$inject = [];
uiGridCellnavDirective.$inject = ['uiGridCellNavService'];
GridStateFactory.$inject = ['$q', '$timeout', 'uiGridSaveStateService'];
runExporterFieldCallbackFix.$inject = ['$injector', '$filter'];

/*******************************************************************************
 * uiGridCellnav (stacked directive) */
/**
 * @ngdoc      directive
 * @name       csm.vendors.ui-grid:uiGridCellnav
 * @restrict   A
 * @priority   -200  (lower than the original so its pre-link runs after it and has
 *                    access to the already-assigned `gridController.cellNav.handleKeyDown`).
 *
 * @description
 * Wraps `gridController.cellNav.handleKeyDown` to neutralize a behavior change
 * introduced in angular-ui-grid 4.12.7. In 4.10.2, the `getRowColDown` and
 * `getRowColRight` navigation helpers returned `null` when the user pressed
 * Enter/Tab/ArrowDown while already on the last row (or wrapped to the same
 * cell). 4.12.7 returns the same `GridRowColumn` instead, causing
 * `handleKeyDown` to schedule a phantom
 * `scrollToIfNecessary(...).then(broadcastCellNav(sameCell, null, keydownEvt))`.
 *
 * When `enableCellEditOnFocus` is true, that phantom CELL_NAV_EVENT triggers
 * the navigate listener (registered by `registerBeginEditEvents`) on the cell
 * that JUST committed, putting it right back into edit mode. The subsequent
 * focus shift then blurs the re-mounted typeahead with no list selection,
 * which the bootstrap typeahead reset clears to null — wiping the user-typed
 * value and breaking the csmGrid autorow promotion.
 *
 * Our override: when the computed next cell equals the current focused cell
 * (i.e., no actual movement), consume the event and return `false` so the
 * caller's `stopEdit` still commits, but SKIP the phantom broadcast entirely.
 * Real navigation paths (movement to a different row/col, and the wrap-around
 * Tab/Shift+Tab cellNav-exit branches which change the col) are delegated to
 * the original `handleKeyDown` unchanged.
 *******************************************************************************/
/* @ngInject */ function uiGridCellnavDirective(uiGridCellNavService) {
  return {
    restrict: 'A',
    require: '^uiGrid',
    priority: -200,
    compile: function () {
      return {
        pre: function ($scope, $elm, $attrs, gridController) {
          /*------------------------------------------------------------------*/
          /* The original handleKeyDown was assigned by the built-in          */
          /* uiGridCellnav directive's pre-link (higher priority ran first).  */
          /*------------------------------------------------------------------*/
          if (!gridController || !gridController.cellNav) return;

          var originalHandleKeyDown = gridController.cellNav.handleKeyDown;
          if (typeof originalHandleKeyDown !== 'function') return;

          gridController.cellNav.handleKeyDown = function (evt) {
            var direction = uiGridCellNavService.getDirection(evt);

            /*--------------------------------------------------*/
            /* Not a nav key — preserve original `null` return. */
            /*--------------------------------------------------*/
            if (direction === null) return null;

            var grid = gridController.grid;
            var containerId = evt.uiGridTargetRenderContainerId || 'body';
            var lastRowCol = grid.api.cellNav.getFocusedCell();
            var renderContainer =
              grid.renderContainers && grid.renderContainers[containerId];

            if (lastRowCol && renderContainer && renderContainer.cellNav) {
              var rowCol = renderContainer.cellNav.getNextRowCol(
                direction,
                lastRowCol.row,
                lastRowCol.col
              );

              /*------------------------------------------------------------*/
              /* No movement — 4.12.7's getRowCol* returned the same cell.  */
              /* Consume the event so the caller's stopEdit still commits,  */
              /* but do NOT fire scrollToIfNecessary/broadcastCellNav. That */
              /* phantom broadcast would otherwise re-enter edit on the     */
              /* just-committed cell when enableCellEditOnFocus is true.    */
              /*------------------------------------------------------------*/
              if (
                rowCol &&
                rowCol.row === lastRowCol.row &&
                rowCol.col === lastRowCol.col
              ) {
                evt.stopPropagation();
                evt.preventDefault();
                return false;
              }
            }

            /*--------------------------------------------------------------*/
            /* Real movement (and the Tab/Shift+Tab cellNav-exit branches,  */
            /* which produce a different col) fall through to the original. */
            /*--------------------------------------------------------------*/
            return originalHandleKeyDown.call(this, evt);
          };
        }
      };
    }
  };
}

/*******************************************************************************
 * Provide a conditional decorator for the Grid constructor factory which handles
 * resizing based on the minRowsToShow and a new grid option maxRowsToShow
 *******************************************************************************/
/* @ngInject */ function vendorsGridConfig($injector, $provide) {
  if ($injector.has('Grid')) {
    $provide.decorator('Grid', function ($delegate, gridUtil, uiGridConstants) {
      /** Original renderingComplete method. */
      var mBaseRenderComplete = $delegate.prototype.renderingComplete;

      /*************************************************************************
       * autoAdjustHeight */
      /**
       * This is the same function from the uiGrid directive except it has been
       * modified to work statically and to use the provided rowCount to base
       * the height of the grid.
       *
       * @param grid     Grid object
       * @param rowCount Rows to draw the grid for
       *************************************************************************/
      function autoAdjustHeight(grid, rowCount) {
        /*---------------------------*/
        /* Figure out the new height */
        /*---------------------------*/
        var contentHeight = rowCount * grid.options.rowHeight;
        var headerHeight = grid.options.showHeader
          ? grid.options.headerRowHeight
          : 0;
        var footerHeight = grid.calcFooterHeight();

        var scrollbarHeight = 0;
        if (
          grid.options.enableHorizontalScrollbar ===
          uiGridConstants.scrollbars.ALWAYS
        )
          scrollbarHeight = gridUtil.getScrollbarWidth();

        /*---------------------------------------------------------*/
        /* Calculates the maximum number of filters in the columns */
        /*---------------------------------------------------------*/
        var maxNumberOfFilters = 0;
        angular.forEach(grid.options.columnDefs, function (col) {
          if (col.hasOwnProperty('filter')) {
            if (maxNumberOfFilters < 1) maxNumberOfFilters = 1;
          } else if (col.hasOwnProperty('filters')) {
            if (maxNumberOfFilters < col.filters.length)
              maxNumberOfFilters = col.filters.length;
          }
        });

        if (grid.options.enableFiltering && !maxNumberOfFilters) {
          var allColumnsHaveFilteringTurnedOff =
            grid.options.columnDefs.length &&
            grid.options.columnDefs.every(function (col) {
              return col.enableFiltering === false;
            });

          if (!allColumnsHaveFilteringTurnedOff) maxNumberOfFilters = 1;
        }

        var filterHeight = maxNumberOfFilters * headerHeight;
        var newHeight =
          headerHeight +
          contentHeight +
          footerHeight +
          scrollbarHeight +
          filterHeight;

        grid.element.css('height', newHeight + 'px');
        grid.gridHeight = grid.directiveScope.gridHeight =
          gridUtil.elementHeight(grid.element);
      }

      /*************************************************************************
       * getCellValue */
      /**
       * Override base method to allow the use of a labelFunction property if
       * available on a column definition object. The function will be provided
       * the data model for the row and the current GridColumn object.
       *
       * @param  row   GridRow reference for the current row
       * @param  col   GridColumn reference for the current column
       * @return       Cell value
       *************************************************************************/
      var mGetCellValue = $delegate.prototype.getCellValue;
      $delegate.prototype.getCellValue = function (row, col) {
        if (col.colDef && col.colDef.labelFunction)
          return col.colDef.labelFunction(row.entity, col);
        else return mGetCellValue.call(this, row, col);
      };

      /*************************************************************************
       * renderingComplete */
      /**
       * Override the original method to check for changes in the data length
       * and see if maxRowsToShow has been set. If so, set a watch expression
       * to update the size of the grid
       *************************************************************************/
      $delegate.prototype.renderingComplete = function () {
        var self = this;
        self.directiveScope = angular
          .element(this.element.children()[0])
          .scope();

        /*---------------------------------------------------*/
        /* Set up a watch if the maxRowsToShow option has    */
        /* been set. When a change in the rowCount is found, */
        /* the grid is resized to fit the requested content  */
        /*---------------------------------------------------*/
        if (this.options.maxRowsToShow != undefined) {
          self.directiveScope.$watch(
            function () {
              return Math.max(
                self.options.minRowsToShow,
                Math.min(self.rows.length, self.options.maxRowsToShow)
              );
            },
            function (rowCount, oldCount) {
              if (rowCount != oldCount) autoAdjustHeight(self, rowCount);
            }
          );
        }

        /*---------------*/
        /* Call the base */
        /*---------------*/
        mBaseRenderComplete.apply(this);
      };

      return $delegate;
    });
  }
}

/*******************************************************************************
 * Decorator: uiGridCellNavService
 *
 * Provide a conditional decorator for the uiGridCellNavService that registers a
 * viewPortKeyDown event handler. When the space key is pressed when the grid has
 * focus, a click is triggered on the first child element with the marker
 * class, "csm-grid-space-target".
 *
 * To use, add the class="csm-grid-space-target" to an element in the ui-grid
 * cellTemplate that should be clicked when the space key is pressed.
 *******************************************************************************/
/* @ngInject */ function uiGridCellNavServiceConfig($injector, $provide) {
  var service = 'uiGridCellNavService';

  if ($injector.has(service)) {
    $provide.decorator(service, function ($delegate, KEYCODES) {
      var originalInitialize = $delegate.initializeGrid;

      $delegate.initializeGrid = function (grid) {
        /*---------------------------------*/
        /* Run the base initialize method. */
        /*---------------------------------*/
        originalInitialize.apply($delegate, arguments);

        /*************************************************************************
         * viewPortKeyDown */
        /**
         * Register the ViewPortKeyDown event handler to listen for a
         * space keydown event in the grid.
         *
         * Note: The event will be triggered by a target element that is not on the
         * same node tree as the grid rows. To trigger a click on the correct
         * element, we need find the ui-grid-contents-wrapper (which is the parent
         * element that contains all rows and target element) and query for the
         * active/focused cell indicated by the class, 'ui-grid-cell-focus'. If the
         * active cell contains a child with the marker class, 'csm-grid-space-target',
         * a click will be manually triggered on the first occurance of the marked element.
         *
         * @param event     Event object
         * @param rowCol    Grid row and column position of keydown event
         *
         * Note: .on.viewPortKeyDown requires either a real Angular scope (an
         * object with $on, so it can deregister the listener on $destroy) or
         * null. Some grids configure appScopeProvider as a plain object (e.g.
         * { $ctrl } from a component controller) rather than a $scope, so we
         * only pass grid.appScope through when it actually looks like a scope
         * - otherwise ui-grid logs a "scope wasn't passed" error.
         *************************************************************************/
        var viewPortKeyDownScope =
          grid.appScope && typeof grid.appScope.$on === 'function'
            ? grid.appScope
            : null;

        grid.api.cellNav.on.viewPortKeyDown(
          viewPortKeyDownScope,
          function (event, rowCol) {
            /*----------------------------------*/
            /* Ignore all keys but the spacebar */
            /*----------------------------------*/
            if (event.keyCode != KEYCODES.SPACE) return;

            /*------------------------------------------------------*/
            /* Get the wrapper element that holds all grid contents */
            /*------------------------------------------------------*/
            var parentElem = event.target;
            do {
              parentElem = parentElem.parentElement;
            } while (
              parentElem &&
              parentElem.className.indexOf('ui-grid-contents-wrapper') == -1
            );

            /*-----------------------------------------*/
            /* Exit if no parent wrapper element found */
            /*-----------------------------------------*/
            if (!parentElem) return;

            /*----------------------------------------------------*/
            /* Get the grid cell element that currently has focus */
            /*----------------------------------------------------*/
            var focusedElem = parentElem.querySelector('.ui-grid-cell-focus');

            /*----------------------------*/
            /* Exit if no cell is focused */
            /*----------------------------*/
            if (!focusedElem) return;

            /*------------------------------------------------------*/
            /* Get the first marked element to trigger the click on */
            /*------------------------------------------------------*/
            var clickElem = focusedElem.querySelector('.csm-grid-space-target');

            /*-----------------------------------------------------*/
            /* Trigger a click on the marked element if available. */
            /* Prevent the event propagation so the paged won't be */
            /* scrolled when the click is processed.               */
            /*-----------------------------------------------------*/
            if (clickElem) {
              event.stopImmediatePropagation();
              event.preventDefault();
              clickElem.click();
            }
          }
        );
      };

      /*------------------------------*/
      /* Return the decorated service */
      /*------------------------------*/
      return $delegate;
    });
  }
}

/*******************************************************************************
 * Decorator: rowSorter
 *
 * Applies two upgrade-related overrides to the ui-grid rowSorter:
 *
 * 1. handleNulls (TBUGS-233) - restores the null-handling behavior where empty
 *    strings and NaN are sorted to the bottom for ASC and top for DESC
 *    alongside null and undefined.
 *
 * 2. guessSortFn (GR-2389) - restores the tolerant behavior for unknown column
 *    `type` values. See the inline comment on the override below.
 *
 * --- 1. handleNulls (TBUGS-233) ---
 * Restores the null-handling behavior where empty strings and NaN are sorted to
 * the bottom for ASC and top for DESC alongside null and undefined,
 * rather than passing through to the normal comparison function.
 *
 * The reason we added this in is because when we updated our angular-ui-grid from
 * version 4.10.2 -> 4.12.7, we noticed that the sorting functionality was a bit
 * different from what it used to be. In the newer version, when you sort a column
 * that has empty string or NaN values, those values are getting sorted to the top
 * for ASC and then the null/undefined values were getting sorted to the top for
 * DESC. So to the user it always seemed like we had blank in the top. This code
 * below takes the falsy checker they had in the sorting code and applies it back
 * with the legacy version. This is the EXACT code that was in the older version.
 *
 * angular-ui-grid commit in question: https://github.com/angular-ui/ui-grid/commit/bb68cab0d8015e546039a664d1796e9810624570
 * TBUGS-233: https://graystep.atlassian.net/browse/TBUGS-233
 *******************************************************************************/
/* @ngInject */ function rowSorterConfig($injector, $provide) {
  if ($injector.has('rowSorter')) {
    $provide.decorator('rowSorter', function ($delegate) {
      $delegate.handleNulls = function handleNulls(a, b) {
        if ((!a && a !== 0 && a !== false) || (!b && b !== 0 && b !== false)) {
          if (!a && a !== 0 && a !== false && !b && b !== 0 && b !== false) {
            return 0;
          } else if (!a && a !== 0 && a !== false) {
            return 1;
          }
          return -1;
        }
        return null;
      };

      /*-----------------------------------------------------------------------*/
      /* GR-2389: guessSortFn                                                  */
      /*                                                                       */
      /* ui-grid only recognizes six column `type` values: string, number,    */
      /* numberStr, boolean, date, object. In 4.10.2 an unrecognized type fell */
      /* through and the grid still sorted; in 4.12.7 the default branch of    */
      /* guessSortFn throws "No sorting function found for type: <type>",       */
      /* crashing the grid the moment such a column is sorted.                 */
      /*                                                                       */
      /* Column definitions across the repo have been corrected to valid types */
      /* (see GR-2389), but this override restores the tolerant behavior so    */
      /* any stray or legacy type falls back to sortAlpha instead of throwing. */
      /*-----------------------------------------------------------------------*/
      $delegate.guessSortFn = function guessSortFn(itemType) {
        switch (itemType) {
          case 'number':
            return $delegate.sortNumber;
          case 'numberStr':
            return $delegate.sortNumberStr;
          case 'boolean':
            return $delegate.sortBool;
          case 'string':
            return $delegate.sortAlpha;
          case 'date':
            return $delegate.sortDate;
          case 'object':
            return $delegate.basicSort;
          default:
            return $delegate.sortAlpha;
        }
      };

      return $delegate;
    });
  }
}

/*******************************************************************************
 * Service: CommonUIGridTemplates
 *
 * Contains common templates to be used with the uiGrid. Events will be dispatched
 * on the appScope for click handlers. Tools is injected to ensure string formatting
 * has been set up.
 *******************************************************************************/
/* @ngInject */ function CommonUIGridTemplatesService(Tools) {
  /*-------------*/
  /* Event Types */
  /*-------------*/
  this.EVENT_DELETE_ROW = '$deleteGridRow';

  /*--------------------------------------------*/
  /* Template for building editableCellTemplate */
  /*--------------------------------------------*/
  this.makeEditorCellTemplate = function (attrs, inputType) {
    return (
      '<div tabindex="0" style="display:block; height:100%;">' +
      '<form name="inputForm" style="display:block; height:100%;">' +
      '<input {0} type="{1}" ng-class="\'colt\' + col.uid" ui-grid-edit-input ng-model="MODEL_COL_FIELD" style="display:block; height:100%;" />' +
      '</form>' +
      '</div>'
    ).format(attrs, inputType || 'text');
  };

  /*--------------------------------------------------*/
  /* Editable cell template meant for percent values. */
  /*--------------------------------------------------*/
  this.CELL_PERCENT_INPUT = this.makeEditorCellTemplate(
    'percent-input percent-precision="col.colDef.percentPrecision"'
  );

  /*-------------------------------------------------*/
  /* Editable cell template meant for currency input */
  /*-------------------------------------------------*/
  this.CELL_CURRENCY_INPUT = this.makeEditorCellTemplate(
    'currency-input currency-symbol="col.colDef.currencySymbol"'
  );

  /*---------------------------------------------------------*/
  /* Default editable cell template with height set to 100%. */
  /*---------------------------------------------------------*/
  this.CELL_DEFAULT = this.makeEditorCellTemplate('', 'INPUT_TYPE');

  /*-----------------------------------------------------------------------*/
  /* Delete Row Cell - Dispatches event with grid and row reference on the */
  /* column definition appScope reference if provided otherwise it will    */
  /* use the grid appScope reference. The csm-grid-space-target class is a */
  /* marker used with the modified ui-grid ViewPortKeyDown event listener. */
  /*-----------------------------------------------------------------------*/
  this.CELL_DELETE_ROW = (
    '<div class="ui-grid-cell-contents ng-binding ng-scope"> ' +
    '<span ng-style="{\'display\':row.entity.hideDelete() ? \'none\' : \'block\'}" style="cursor:pointer" class="glyphicon glyphicon-trash csm-grid-space-target" ng-click="(col.colDef.appScope || grid.appScope).$broadcast(\'{0}\', grid, row)"></span>' +
    '</div>'
  ).format(this.EVENT_DELETE_ROW);

  /*--------------------------------------------------------------*/
  /* Editable cell template meant for numeric values. This is the */
  /* default grid cell edit template to update model on a blur.   */
  /*--------------------------------------------------------------*/
  this.makeNumberEditorCellTemplate = function (attrs) {
    return this.makeEditorCellTemplate(
      'number-input precision="col.colDef.precision" blank-is-nan="col.colDef.blankIsNan" allow-negative="col.colDef.allowNegative" {0}'.format(
        attrs
      )
    );
  };

  this.CELL_NUMBER_INPUT = this.makeNumberEditorCellTemplate('');
}

/*******************************************************************************
 * uiGridEditInput */
/**
 * @ngdoc directive
 * @name csm.vendors.ui-grid:uiGridEditInput
 * @element div
 * @restrict A
 *
 * @description input editor directive for editable fields.
 * Provides EndEdit and CancelEdit events.
 *
 * Events that end editing:
 *   blur and enter keydown
 *
 * Events that cancel editing:
 *   - Esc keydown
 *
 * This is a recreation of the uiGridEditor to remove the isolated scope. It was
 * causing issues when used with other attribute directives that tried using an
 * isolated scope, or tried sharing the scope, causing an error to be thrown.
 *******************************************************************************/
/* @ngInject */ function uiGridEditInputDirective(
  gridUtil,
  uiGridConstants,
  uiGridEditConstants,
  $timeout,
  uiGridEditService
) {
  function post($scope, $elm, $attrs, controllers) {
    var uiGridCtrl, renderContainerCtrl, ngModel;
    if (controllers[0]) {
      uiGridCtrl = controllers[0];
    }
    if (controllers[1]) {
      renderContainerCtrl = controllers[1];
    }
    if (controllers[2]) {
      ngModel = controllers[2];
    }

    /*----------------------------*/
    /* set focus at start of edit */
    /*----------------------------*/
    $scope.$on(
      uiGridEditConstants.events.BEGIN_CELL_EDIT,
      function (evt, triggerEvent) {
        $timeout(function () {
          $elm[0].focus();
          /*----------------------------------------------*/
          /* Only select text if it is not being replaced */
          /* below in the cellNav viewPortKeyPress.       */
          /*----------------------------------------------*/
          if (
            $elm[0].select &&
            ($scope.col.colDef.enableCellEditOnFocus ||
              !(uiGridCtrl && uiGridCtrl.grid.api.cellNav))
          )
            $elm[0].select();
          /*-----------------------------------------------------------*/
          /* Some browsers (Chrome) stupidly, imo, support the w3      */
          /* standard that number, email, ... fields should not allow  */
          /* setSelectionRange. We ignore the error for those browsers */
          /* https://www.w3.org/Bugs/Public/show_bug.cgi?id=24796      */
          /*-----------------------------------------------------------*/ else
            try {
              $elm[0].setSelectionRange(
                $elm[0].value.length,
                $elm[0].value.length
              );
            } catch (ex) {}
        });

        /*-------------------------------------------------------*/
        /* Set the keystroke that started the edit event.        */
        /* we must do this because the BeginEdit is done in a    */
        /* different event loop than the intitial keydown event. */
        /* Fire this event for the keypress that is received.    */
        /*-------------------------------------------------------*/
        if (uiGridCtrl && uiGridCtrl.grid.api.cellNav) {
          var viewPortKeyDownUnregister =
            uiGridCtrl.grid.api.cellNav.on.viewPortKeyPress(
              $scope,
              function (evt, rowCol) {
                if (uiGridEditService.isStartEditKey(evt)) {
                  ngModel.$setViewValue(
                    String.fromCharCode(
                      typeof evt.which === 'number' ? evt.which : evt.keyCode
                    ),
                    evt
                  );
                  ngModel.$render();
                }
                viewPortKeyDownUnregister();
              }
            );
        }

        /*---------------------------------------------------------------------*/
        /* macOS will blur the checkbox when clicked in Safari and Firefox, to */
        /* get around this, we disable the blur handler on mousedown, and then */
        /* focus the checkbox and re-enable the blur handler after $timeout.   */
        /*---------------------------------------------------------------------*/
        $elm.on('mousedown', function (evt) {
          if ($elm[0].type === 'checkbox') {
            $elm.off('blur', $scope.stopEdit);
            $timeout(function () {
              $elm[0].focus();
              $elm.on('blur', $scope.stopEdit);
            });
          }
        });

        $elm.on('blur', $scope.stopEdit);
      }
    );

    $scope.deepEdit = false;
    $scope.stopEdit = function (evt) {
      if ($scope.inputForm && !$scope.inputForm.$valid) {
        evt.stopPropagation();
        $scope.$emit(uiGridEditConstants.events.CANCEL_CELL_EDIT);
      } else $scope.$emit(uiGridEditConstants.events.END_CELL_EDIT);
      $scope.deepEdit = false;
    };

    $elm.on('click', function (evt) {
      if ($elm[0].type !== 'checkbox') {
        $scope.deepEdit = true;
        $timeout(function () {
          $scope.grid.disableScrolling = true;
        });
      }
    });

    $elm.on('keydown', function (evt) {
      switch (evt.keyCode) {
        case uiGridConstants.keymap.ESC:
          evt.stopPropagation();
          $scope.$emit(uiGridEditConstants.events.CANCEL_CELL_EDIT);
          break;
      }

      if (
        $scope.deepEdit &&
        (evt.keyCode === uiGridConstants.keymap.LEFT ||
          evt.keyCode === uiGridConstants.keymap.RIGHT ||
          evt.keyCode === uiGridConstants.keymap.UP ||
          evt.keyCode === uiGridConstants.keymap.DOWN)
      )
        evt.stopPropagation();
      /*-----------------------------------------------------------------*/
      /* Pass the keydown event off to the cellNav service, if it exists */
      /*-----------------------------------------------------------------*/ else if (
        uiGridCtrl &&
        uiGridCtrl.grid.api.cellNav
      ) {
        evt.uiGridTargetRenderContainerId = renderContainerCtrl.containerId;
        if (uiGridCtrl.cellNav.handleKeyDown(evt) !== null)
          $scope.stopEdit(evt);
      } else if (
        /*----------------------------------------------------*/
        /* handle enter and tab for editing not using cellNav */
        /*----------------------------------------------------*/
        evt.keyCode === uiGridConstants.keymap.ENTER ||
        evt.keyCode === uiGridConstants.keymap.TAB
      ) {
        evt.stopPropagation();
        evt.preventDefault();
        $scope.stopEdit(evt);
      }

      return true;
    });

    $scope.$on('$destroy', function unbindEvents() {
      /*----------------------------------------------------------*/
      /* Unbind all jquery events in order to avoid memory leaks. */
      /*----------------------------------------------------------*/
      $elm.off();
    });
  }

  return {
    require: ['?^uiGrid', '?^uiGridRenderContainer', 'ngModel'],
    compile: function () {
      return { pre: function ($scope, $elm, $attrs) {}, post: post };
    }
  };
}

/*******************************************************************************
 * Directive: uiGridEditor */
/**
 * A utility directive to allow elements to fire a blur event when the cell has
 * completed editing.
 *******************************************************************************/
/* @ngInject */ function uiGridEditorDirective(uiGridEditConstants) {
  return {
    link: function (scope, element, attrs) {
      /*-------------------------------------*/
      /* When the cell edit has ended, force */
      /* a blur on the internal element.     */
      /*-------------------------------------*/
      scope.$on(uiGridEditConstants.events.END_CELL_EDIT, function () {
        element[0].blur();
      });
    }
  };
}

/*******************************************************************************
 * Directive: uiGridEditTypeahead
 *
 * A utility directive to be applied to uibTypeahead edit components used with
 * the uiGrid directive. This directive will prevent certain key press events from
 * propagating to the uiGrid components to allow the typeahead to function.
 *******************************************************************************/
/* @ngInject */ function uiGridEditTypeaheadDirective(
  uiGridEditConstants,
  $timeout,
  KEYCODES
) {
  /** List scroll arrow keys. */
  var mArrowKeys = [KEYCODES.UP_ARROW, KEYCODES.DOWN_ARROW];

  /** End edit keys. */
  var mEndEditKeys = [KEYCODES.ENTER, KEYCODES.TAB];

  return {
    priority: -10,
    require: ['?^uiGrid', '?^uiGridRenderContainer', 'uibTypeahead'],
    scope: true,
    link: function ($scope, element, $attrs, controllers) {
      var uiGridCtrl = controllers[0];
      var renderContainerCtrl = controllers[1];

      /*************************************************************************
       * endEdit */
      /**
       * Finish the editting of the cell, emits either a end or cancel edit event
       * on the scope.
       *
       * @param  cancel   Indication to emit a cancel edit
       *************************************************************************/
      function endEdit(cancel) {
        /*------------------------------------------------------------------*/
        /* Allow the internal input to blur before the editor is destroyed. */
        /*------------------------------------------------------------------*/
        if (!cancel) {
          element[0].blur();
          element.off('blur');
        }

        $scope.$emit(
          cancel
            ? uiGridEditConstants.events.CANCEL_CELL_EDIT
            : uiGridEditConstants.events.END_CELL_EDIT
        );
      }

      /*-------------------------------------------------------------*/
      /* Set focus to input on begin edit wait until the next digest */
      /* cycle to ensure the content has been added to the DOM       */
      /*-------------------------------------------------------------*/
      $scope.$on(uiGridEditConstants.events.BEGIN_CELL_EDIT, function () {
        $timeout(function () {
          element[0].focus();
          element[0].style.width =
            element[0].parentElement.offsetWidth - 1 + 'px';
          element.on('blur', function () {
            endEdit();
          });
        });
      });

      /*------------------------------------*/
      /* Process key presses to allow for   */
      /* the type ahead to maintain control */
      /*------------------------------------*/
      element.on('keydown', function (event) {
        /*----------------------------------------*/
        /* Stop propagation to cell nav listeners */
        /* so typeahead can pick up presses       */
        /*----------------------------------------*/
        if (mArrowKeys.indexOf(event.keyCode) > -1) {
          event.stopPropagation();
          return;
        } else if (event.keyCode == KEYCODES.ESCAPE) {
          /*------------------------------*/
          /* Escape will trigger a cancel */
          /*------------------------------*/
          event.stopPropagation();
          event.preventDefault();
          endEdit(true);
        } else if (uiGridCtrl && uiGridCtrl.grid.api.cellNav) {
          /*---------------------------------------------------------------*/
          /* If the cellNav module is being used, let it process the event */
          /*---------------------------------------------------------------*/
          event.uiGridTargetRenderContainerId = renderContainerCtrl.containerId;
          if (uiGridCtrl.cellNav.handleKeyDown(event) !== null) endEdit();
        } else if (mEndEditKeys.indexOf(event.keyCode) > -1) {
          /*----------------------------------------------*/
          /* Process key presses for ending the editting. */
          /* When the cellNav module is not available     */
          /*----------------------------------------------*/
          event.stopPropagation();
          event.preventDefault();
          endEdit();
        }
      });
    }
  };
}

/*******************************************************************************
 * Directive: uiGridEditLazyTypeahead
 *
 * A utility directive to be applied to lazyTypeahead components used with the
 * uiGrid directive. This directive will prevent certain key press events from
 * propagating to the uiGrid components to allow the lazyTypeahead to function.
 *******************************************************************************/
/* @ngInject */ function uiGridEditLazyTypeaheadDirective(
  $compile,
  $parse,
  $timeout,
  uiGridEditConstants,
  KEYCODES
) {
  /** List scroll arrow keys. */
  var mArrowKeys = [KEYCODES.UP_ARROW, KEYCODES.DOWN_ARROW];

  /** End edit keys. */
  var mEndEditKeys = [KEYCODES.ENTER, KEYCODES.TAB];

  return {
    priority: -10,
    require: ['?^uiGrid', '?^uiGridRenderContainer', 'lazyTypeahead'],
    link: function ($scope, element, $attrs, controllers) {
      var uiGridCtrl = controllers[0];
      var renderContainerCtrl = controllers[1];
      var lazyTypeaheadCtrl = controllers[2];

      /*************************************************************************
       * endEdit */
      /**
       * Finish the editting of the cell, emits either an end or cancel edit
       * event on the scope.
       *
       * @param  cancel   Indication to emit a cancel edit
       *************************************************************************/
      function endEdit(cancel) {
        /*-------------------------------------------------*/
        /* We only want to call this enter command if this */
        /* isn't from a cancel event. Triggering a blur on */
        /* the component so any listeners can respond      */
        /*-------------------------------------------------*/
        if (!cancel) {
          element[0].blur();
          lazyTypeaheadCtrl.handleEnter();
        }

        $scope.$emit(
          cancel
            ? uiGridEditConstants.events.CANCEL_CELL_EDIT
            : uiGridEditConstants.events.END_CELL_EDIT
        );
      }

      /*----------------------------------------------------*/
      /* Set up listener for when the lazyTypeahead blurs   */
      /* so that we can tell the grid to end the cell edit. */
      /*----------------------------------------------------*/
      lazyTypeaheadCtrl.addEventListener('ltOnBlur', function (event) {
        $scope.$emit(uiGridEditConstants.events.END_CELL_EDIT);
      });

      /*-------------------------------------------------------------*/
      /* Set focus to input on begin edit wait until the next digest */
      /* cycle to ensure the content has been added to the DOM       */
      /*-------------------------------------------------------------*/
      $scope.$on(uiGridEditConstants.events.BEGIN_CELL_EDIT, function () {
        $timeout(function () {
          element[0].focus();
          element[0].style.width =
            element[0].parentElement.offsetWidth - 1 + 'px';
          element[0].style.padding = '0px';

          /*--------------------------------------------------------*/
          /* Adjust the padding for both the lazyTypeahead, and     */
          /* its input to make the input fill the entire row space. */
          /*--------------------------------------------------------*/
          var input = element.find('input')[0];
          input.style.height = '100%';
          input.style.paddingLeft = '5px';
        });
      });

      /*------------------------------------*/
      /* Process key presses to allow for   */
      /* the type ahead to maintain control */
      /*------------------------------------*/
      element.on('keydown', function (event) {
        /*----------------------------------------*/
        /* Stop propagation to cell nav listeners */
        /* so typeahead can pick up presses       */
        /*----------------------------------------*/
        if (mArrowKeys.indexOf(event.keyCode) > -1) {
          event.stopPropagation();
          return;
        } else if (event.keyCode == KEYCODES.ESCAPE) {
          /*------------------------------*/
          /* Escape will trigger a cancel */
          /*------------------------------*/
          event.stopPropagation();
          event.preventDefault();
          endEdit(true);
        } else if (uiGridCtrl && uiGridCtrl.grid.api.cellNav) {
          /*---------------------------------------------------------------*/
          /* If the cellNav module is being used, let it process the event */
          /*---------------------------------------------------------------*/
          event.uiGridTargetRenderContainerId = renderContainerCtrl.containerId;
          if (uiGridCtrl.cellNav.handleKeyDown(event) !== null) endEdit();
        } else if (mEndEditKeys.indexOf(event.keyCode) > -1) {
          /*----------------------------------------------*/
          /* Process key presses for ending the editting. */
          /* When the cellNav module is not available     */
          /*----------------------------------------------*/
          endEdit();
          event.stopPropagation();
          event.preventDefault();
        }
      });
    }
  };
}

/*****************************************************************************
 * Directive: uiGridHeaderCell
 *
 * Prevent grid column headers from receiving focus while tabbing through
 * document
 * src: https://github.com/angular-ui/ui-grid/issues/4951#issuecomment-231588305
 *****************************************************************************/
/* @ngInject */ function uiGridHeaderCellDirective() {
  return {
    restrict: 'C',
    link: function (scope, element, attrs) {
      element.find('[role="button"]').removeAttr('tabindex');
    }
  };
}

/*******************************************************************************
 * Constructor Factory: GridState */
/**
 * @ngdoc  service
 * @module csm.vendors.ui-grid
 * @name   csm.vendors.ui-grid.GridState.service:GridState
 *
 * @description
 * This returns a constructor to generate an object that helps with saving and
 * restoring uiGrid data utilizing the <b>uiGridSaveStateService</b>. The use
 * of this factory requires the "ui.grid.saveState" module.
 *******************************************************************************/
/* @ngInject */ function GridStateFactory(
  $q,
  $timeout,
  uiGridSaveStateService
) {
  /*****************************************************************************
   * Constructor */
  /**
   * @ngdoc       method
   * @constructs  csm.vendors.ui-grid.GridState
   * @description
   * Establishes the object to save the data
   ******************************************************************************/
  function GridState() {
    /** Stored grid state data. */ this.gridData = null;
  }

  /*****************************************************************************
   * clearData */
  /**
   * @ngdoc    method
   * @name     clearData
   * @methodOf csm.vendors.ui-grid.GridState.service:GridState
   *
   * @description
   * Removes any saved state data
   *****************************************************************************/
  GridState.prototype.clearData = function () {
    mGridData = null;
  };

  /*****************************************************************************
   * init */
  /**
   * @ngdoc    method
   * @name     init
   * @methodOf csm.vendors.ui-grid.GridState.service:GridState
   * @description
   * This method is use to restore the saved grid data when a grid has been
   * initialized and set up a listener to store the grid state data when the
   * grid DOM element is removed. This is accomplished by using the
   * **onRegisterApi** propery of the gridOptions object. If this property has
   * already been defined, it will be overridden but still called.
   *
   * In addition, this will look utilize a notification property function, **onRestored**,
   * on gridOptions to be called when the state data has been restored.
   *
   * @param {object} gridOptions  UiGrid configuration options object
   *****************************************************************************/
  GridState.prototype.init = function (gridOptions) {
    var mThis = this;

    /*-------------------------------------*/
    /* Get original onRegisterApi function */
    /*-------------------------------------*/
    var baseRegister = gridOptions.onRegisterApi || angular.noop;

    /*------------------------------------------*/
    /* Redefine so we can setup the state logic */
    /*------------------------------------------*/
    gridOptions.onRegisterApi = function (api) {
      /*---------------------------------*/
      /* Call the original onRegisterApi */
      /*---------------------------------*/
      baseRegister(api);

      /*-----------------------------------------*/
      /* Restore the data and notify when loaded */
      /*-----------------------------------------*/
      mThis.restore(api.grid).then(gridOptions.onRestored || angular.noop);

      /*-------------------------------------------*/
      /* Set up a listener for when the grid       */
      /* element is removed to save the state data */
      /*-------------------------------------------*/
      api.grid.element.on('$destroy', function () {
        mThis.save(api.grid);
      });
    };
  };

  /*****************************************************************************
   * restore */
  /**
   * @ngdoc    method
   * @name     restore
   * @methodOf csm.vendors.ui-grid.GridState.service:GridState
   * @description
   * This will restore the saved grid data if set on the passed **grid** object.
   * A timeout is used to ensure the grid is loaded and drawn before attempting
   * to set state data. Test cases found that when trying to restore the columns
   * that they were not configured yet when the grid was not shown.
   *
   * @param   {object}  grid  UiGrid object reference
   * @returns {Promise}       Promised that is resolved when the state has been
   *                          restored
   *****************************************************************************/
  GridState.prototype.restore = function (grid) {
    var mThis = this;

    /*------------------------*/
    /* Restore if data exists */
    /*------------------------*/
    if (mThis.gridData) {
      var defer = $q.defer();
      $timeout(function () {
        uiGridSaveStateService.restore(grid, null, mThis.gridData);
        defer.resolve();
      });
      return defer.promise;
    } else return $q.resolve();

    /*------------------------------------------*/
    /* Otherwise just return a resolved promise */
    /*------------------------------------------*/
  };

  /*****************************************************************************
   * save */
  /**
   * @ngdoc    method
   * @name     save
   * @methodOf csm.vendors.ui-grid.GridState.service:GridState
   * @description
   * This will save the state of the provided grid.
   *
   * @param   {object}  grid  UiGrid object reference
   *****************************************************************************/
  GridState.prototype.save = function (grid) {
    this.gridData = uiGridSaveStateService.save(grid);
  };

  return GridState;
}

/*--------------------------------------------*/
/* Add style rules to enhance/fix the ui-grid */
/*--------------------------------------------*/
function runVendorUiGrid() {
  var mBody = angular.element(document.querySelector('body'));
  mBody.append(
    angular.element(
      '<style>' +
        /*-----------------------------------------------*/
        /* Allows the header row to receive mouse events */
        /* which allow for selection by the check mark.  */
        /*-----------------------------------------------*/
        '.ui-grid-cell.ui-grid-disable-selection.ui-grid-row-header-cell { pointer-events: auto;}' +
        /*---------------------------------------------------*/
        /* Fixes scroll issue in Chrome 56 so that scrolling */
        /* flows smoothly instead of jumping down when using */
        /* the mouse wheel or clicking on the down arrow.    */
        /*---------------------------------------------------*/
        '.ui-grid-viewport { overflow-anchor: none }' +
        '</style>'
    )
  );
}

/*******************************************************************************
 * Exporter: default exporterFieldCallback
 *
 * Replaces ui-grid's default exporterFieldCallback, which re-applies the
 * column's cellFilter to each exported value. In angular-ui-grid 4.12.7 the
 * default callback passes `null` for any filter argument that isn't specified
 * in the cellFilter string. AngularJS filters only fall back to their defaults
 * for `undefined`, so `cellFilter: 'currency'` became
 * `$filter('currency')(0.95, null, null)` and exported as "null0.95" instead
 * of "$0.95". This is the same parsing as 4.12.7, but missing arguments are
 * passed as `undefined` (the 4.10.2 behavior).
 *
 * Done in a run block (rather than a config decorator) so it works regardless
 * of whether the app lists 'ui.grid.exporter' before or after CSMCommonWeb.
 * Grids that set their own exporterFieldCallback are left untouched.
 *
 * Also defaults exporterMenuPdf/exporterMenuExcel to false so the grid menu
 * only offers CSV export.
 *******************************************************************************/
/* @ngInject */ function runExporterFieldCallbackFix($injector, $filter) {
  if (!$injector.has('uiGridExporterService')) {
    return;
  }

  const uiGridExporterService = $injector.get('uiGridExporterService');
  const originalDefaultGridOptions = uiGridExporterService.defaultGridOptions;

  uiGridExporterService.defaultGridOptions = function (gridOptions) {
    if (!gridOptions.exporterFieldCallback) {
      gridOptions.exporterFieldCallback = exporterFieldCallback;
    }
    // Only CSV export is offered for now; ui-grid defaults PDF/Excel to on.
    // A grid can still opt back in by setting either option to true.
    if (gridOptions.exporterMenuPdf === undefined) {
      gridOptions.exporterMenuPdf = false;
    }
    if (gridOptions.exporterMenuExcel === undefined) {
      gridOptions.exporterMenuExcel = false;
    }
    return originalDefaultGridOptions.apply(this, arguments);
  };

  /*****************************************************************************
   * exporterFieldCallback */
  /**
   * Formats one cell for export by running the raw value through the column's
   * cellFilter, so the CSV matches what the grid shows (e.g. 0.95 -> "$0.95").
   * Columns without a cellFilter export the raw value unchanged.
   *
   * The cellFilter string is parsed into a filter name and up to two args:
   *   'currency'                  -> currency(value)
   *   'currency:"$":2'            -> currency(value, '$', '2')
   *   'date:"MM/dd/yyyy h:mm a"'  -> date(value, 'MM/dd/yyyy h:mm a')
   *   'number:2'                  -> number(value, '2')
   * Args that aren't in the string are passed as `undefined` (not `null`) so
   * the filter falls back to its own defaults. Numeric args stay strings; the
   * AngularJS filters coerce them.
   *
   * Limitations (same as ui-grid's version): only the first two args are
   * passed, chained filters ('a | b') are not supported, and only double
   * quotes protect a ':' inside an arg (single-quoted 'h:mm' would be split).
   *
   * @param  grid   The ui-grid instance (unused; part of ui-grid's signature).
   * @param  row    The GridRow being exported (unused).
   * @param  col    The GridColumn being exported; supplies the cellFilter.
   * @param  value  The raw cell value to format.
   *****************************************************************************/
  function exporterFieldCallback(grid, row, col, value) {
    if (!col.cellFilter) {
      return value;
    }

    // Split on ':' except inside double quotes, e.g.
    // 'date:"h:mm a"' -> ['date', '"h:mm a"'].
    const args = col.cellFilter.match(/(?:[^:"]+|"[^"]*")+/g);

    // Filter name: strip quotes and all whitespace ('currency ' -> 'currency').
    const filter = args[0].replace(/['"\s]/g, '');

    // First arg (currency symbol, date format, number fraction size): strip
    // quotes but keep inner spaces, since date formats like 'MM/dd/yyyy h:mm a'
    // need them. Only leading/trailing whitespace is trimmed.
    const arg1 = args[1] ? args[1].replace(/['"]/g, '').trim() : undefined;

    // Second arg (currency fraction size, date timezone): strip quotes and all
    // whitespace. The `undefined` fallbacks are the actual fix; ui-grid
    // 4.12.7 uses `null`, which currency renders as the literal symbol "null".
    const arg2 = args[2] ? args[2].replace(/['"\s]/g, '') : undefined;

    return $filter(filter)(value, arg1, arg2);
  }
}
